/**
 * Per-Szene SFX-Rezepte — generieren passende Sound-Beats für eine
 * Hyperframes-Szene basierend auf ihrer Animations-Choreographie.
 *
 * Wird vom Inspector-Button „Passende SFX einsetzen" verwendet. Das Rezept
 * liefert *clip-lokale* Sekunden (`atSec`, 0 = Szenenanfang). Der Composer
 * rechnet das beim Einfügen in absolute Reel-Zeit um, indem es `scene.fromSec`
 * draufaddiert (siehe `applyAutoSfxForScene` in `Composer.tsx`).
 *
 * Konvention:
 *  - Lautstärken bleiben dezent (0.25–0.5), damit das Reel nicht überladen
 *    klingt. Reel-weiter SFX-Master multipliziert oben drauf.
 *  - Jeder Beat bekommt am Ende vom Composer ein `sceneUid`-Tag, damit man
 *    die ganze Gruppe per Inspector-Button wieder entfernen kann.
 */
import type { SceneInputs, SfxName } from "../api";

export type SceneSfxBeatRecipe = {
  /** Clip-lokale Startzeit (0 = Szenenanfang). */
  atSec: number;
  /** Sound-ID aus der SFX-Library. */
  sound: SfxName;
  /** 0..1 — default 0.4. Reel-weiter SFX-Master kommt oben drauf. */
  volume?: number;
  /** Sichtbare Bar-Länge auf der Timeline. Default ergibt sich aus
   *  Sound-Typ (siehe `defaultDurForSound`). */
  durSec?: number;
  /** Optionales Label, das auf der Timeline-Bar angezeigt wird. */
  label?: string;
};

type RecipeFn = (params: {
  sceneId: string;
  inputs: SceneInputs;
  durationSec: number;
}) => SceneSfxBeatRecipe[];

/** Default-Bar-Länge in Sekunden, abhängig vom Sound-Typ. */
export function defaultDurForSound(sound: SfxName): number {
  switch (sound) {
    case "tick":
    case "tap":
    case "click":
    case "pop":
      return 0.3;
    case "ding":
    case "notify":
    case "glass":
    case "swoosh":
    case "woosh":
      return 0.6;
    case "impact":
    case "thud":
      return 0.7;
    case "magic":
      return 1.0;
    case "shimmer":
    case "transition":
      return 1.0;
    case "riser":
      return 1.6;
    case "sub":
      return 1.2;
    case "sfx495":
    case "sfx503":
    case "sfx505":
      return 2.1;
    case "sfx676":
      return 4.1;
    default:
      return 0.6;
  }
}

const clamp = (v: number, min: number, max: number): number => Math.min(max, Math.max(min, v));

/* ============================================================
 * Helper: wie viele „items" hat eine Szene effektiv?
 * ============================================================ */
function itemsLen(inputs: SceneInputs): number {
  return Array.isArray(inputs.items) ? inputs.items.length : 0;
}

/* ============================================================
 *  Recipes per Scene-ID
 * ============================================================ */

const colorBlockRevealRecipe: RecipeFn = ({ inputs, durationSec }) => {
  const items = Array.isArray(inputs.items) ? inputs.items : [];
  const questions = items.filter((q) => {
    if (typeof q !== "string") return false;
    const t = q.trim();
    if (!t) return false;
    const u = t.toUpperCase();
    return u !== "ALLYVER" && u !== "ALLYVER." && t !== "@logo" && u !== "@LOGO";
  });
  const questionsCount = questions.length || 4;
  const blocksCount = questionsCount * 2;
  const slot = Math.max(0.85, durationSec / Math.max(blocksCount, 1));

  const beats: SceneSfxBeatRecipe[] = [];
  for (let i = 0; i < blocksCount; i++) {
    const at = i * slot;
    const isAllyver = i % 2 === 1;
    beats.push({
      atSec: at,
      sound: isAllyver ? "transition" : "swoosh",
      volume: isAllyver ? 0.45 : 0.35,
      label: isAllyver ? `Wisch ALLYVER` : `Wisch Frage`,
    });
    beats.push({
      atSec: at + 0.22,
      sound: isAllyver ? "ding" : "tap",
      volume: isAllyver ? 0.4 : 0.3,
      durSec: isAllyver ? 0.5 : 0.25,
    });
  }
  return beats;
};

const laptopMockupRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.05, sound: "swoosh", volume: 0.4, label: "Device in" },
  { atSec: 0.45, sound: "sub", volume: 0.32, label: "Land" },
  { atSec: 0.85, sound: "shimmer", volume: 0.3, label: "Glow" },
  { atSec: clamp(durationSec - 0.8, 1.0, durationSec), sound: "transition", volume: 0.4, label: "Outro" },
];

const phoneMockupRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.05, sound: "swoosh", volume: 0.38 },
  { atSec: 0.5, sound: "tap", volume: 0.35 },
  { atSec: 1.0, sound: "shimmer", volume: 0.28 },
  { atSec: clamp(durationSec - 0.7, 1.0, durationSec), sound: "transition", volume: 0.36 },
];

const tabletMockupRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.1, sound: "swoosh", volume: 0.38 },
  { atSec: 0.55, sound: "sub", volume: 0.3 },
  { atSec: 1.1, sound: "tap", volume: 0.3 },
  { atSec: clamp(durationSec - 0.7, 1.0, durationSec), sound: "transition", volume: 0.36 },
];

const watchMockupRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.1, sound: "tick", volume: 0.4 },
  { atSec: 0.6, sound: "shimmer", volume: 0.32 },
  { atSec: clamp(durationSec - 0.5, 0.8, durationSec), sound: "ding", volume: 0.35 },
];

const browserMockupRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.05, sound: "click", volume: 0.4, label: "Open" },
  { atSec: 0.6, sound: "tap", volume: 0.3 },
  { atSec: clamp(durationSec - 0.7, 1.0, durationSec), sound: "transition", volume: 0.36 },
];

const signOffRecipe: RecipeFn = ({ durationSec }) => {
  const beats: SceneSfxBeatRecipe[] = [
    { atSec: 0.1, sound: "transition", volume: 0.45, label: "Cinematic in" },
    { atSec: 0.7, sound: "shimmer", volume: 0.4, label: "Logo glow" },
  ];
  if (durationSec >= 2.0) beats.push({ atSec: 1.4, sound: "ding", volume: 0.4, label: "CTA" });
  return beats;
};

const logoOutroRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.05, sound: "transition", volume: 0.45 },
  { atSec: 0.7, sound: "shimmer", volume: 0.4 },
  { atSec: clamp(durationSec - 0.4, 0.8, durationSec), sound: "ding", volume: 0.35 },
];

/* ---------- Intro-Szenen (Logo-Reveals etc.) ---------- */

const introLogoRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.0, sound: "riser", volume: 0.4 },
  { atSec: clamp(durationSec * 0.55, 0.3, durationSec - 0.5), sound: "impact", volume: 0.5, label: "Logo hit" },
  { atSec: clamp(durationSec * 0.55 + 0.08, 0.4, durationSec), sound: "shimmer", volume: 0.35 },
];

const cinematicRevealRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.05, sound: "riser", volume: 0.42 },
  { atSec: clamp(durationSec - 1.3, 0.5, durationSec - 0.3), sound: "transition", volume: 0.5 },
  { atSec: clamp(durationSec - 0.5, 0.6, durationSec), sound: "shimmer", volume: 0.35 },
];

const countdownIntroRecipe: RecipeFn = ({ durationSec }) => {
  const beats: SceneSfxBeatRecipe[] = [];
  const steps = Math.max(3, Math.min(5, Math.round(durationSec)));
  for (let i = 0; i < steps; i++) {
    beats.push({ atSec: i * 1.0, sound: "tick", volume: 0.45, durSec: 0.25 });
  }
  beats.push({ atSec: steps * 1.0 - 0.1, sound: "impact", volume: 0.55, label: "Go" });
  return beats;
};

const brandFlashRecipe: RecipeFn = () => [
  { atSec: 0.0, sound: "impact", volume: 0.5, label: "Flash" },
  { atSec: 0.05, sound: "shimmer", volume: 0.35 },
];

const glitchIntroRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.0, sound: "riser", volume: 0.4 },
  { atSec: 0.4, sound: "thud", volume: 0.45, label: "Glitch" },
  { atSec: 0.65, sound: "thud", volume: 0.4 },
  { atSec: clamp(durationSec - 0.5, 0.8, durationSec), sound: "transition", volume: 0.4 },
];

/* ---------- Text-Szenen ---------- */

const typewriterRecipe: RecipeFn = ({ inputs, durationSec }) => {
  const text = String(inputs.text ?? "");
  const chars = Math.max(1, text.length);
  // Eine Tap pro ~6 Zeichen (zu viele Ticks werden anstrengend).
  const step = Math.max(0.18, (durationSec - 0.6) / Math.ceil(chars / 6));
  const beats: SceneSfxBeatRecipe[] = [];
  for (let t = 0.1; t < durationSec - 0.4; t += step) {
    beats.push({ atSec: t, sound: "tap", volume: 0.22, durSec: 0.2 });
  }
  beats.push({ atSec: clamp(durationSec - 0.3, 0.5, durationSec), sound: "ding", volume: 0.35 });
  return beats;
};

const quoteSlamRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.0, sound: "riser", volume: 0.4 },
  { atSec: 0.55, sound: "impact", volume: 0.55, label: "Slam" },
  { atSec: clamp(durationSec - 0.5, 0.8, durationSec), sound: "transition", volume: 0.35 },
];

const flashStopRecipe: RecipeFn = () => [
  { atSec: 0.0, sound: "impact", volume: 0.55 },
  { atSec: 0.05, sound: "shimmer", volume: 0.32 },
];

const textRevealRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.1, sound: "swoosh", volume: 0.32 },
  { atSec: 0.6, sound: "shimmer", volume: 0.28 },
  { atSec: clamp(durationSec - 0.5, 0.8, durationSec), sound: "transition", volume: 0.32 },
];

/* ---------- List-Szenen ---------- */

const listStackRecipe: RecipeFn = ({ inputs, durationSec }) => {
  const n = Math.max(1, itemsLen(inputs));
  const stagger = clamp((durationSec - 0.8) / n, 0.25, 0.7);
  const beats: SceneSfxBeatRecipe[] = [{ atSec: 0.0, sound: "swoosh", volume: 0.35 }];
  for (let i = 0; i < n; i++) {
    beats.push({ atSec: 0.25 + i * stagger, sound: "tap", volume: 0.3, durSec: 0.25 });
  }
  beats.push({ atSec: clamp(durationSec - 0.5, 0.8, durationSec), sound: "transition", volume: 0.32 });
  return beats;
};

/* ---------- Visual-Szenen mit Items / Cards ---------- */

const itemRevealRecipe: RecipeFn = ({ inputs, durationSec }) => {
  const n = Math.max(1, itemsLen(inputs) || 4);
  const stagger = clamp((durationSec - 0.8) / n, 0.2, 0.6);
  const beats: SceneSfxBeatRecipe[] = [{ atSec: 0.0, sound: "swoosh", volume: 0.35 }];
  for (let i = 0; i < n; i++) {
    beats.push({ atSec: 0.25 + i * stagger, sound: "pop", volume: 0.32, durSec: 0.3 });
  }
  beats.push({ atSec: clamp(durationSec - 0.4, 0.8, durationSec), sound: "shimmer", volume: 0.3 });
  return beats;
};

const cardDeckFanRecipe: RecipeFn = ({ durationSec }) => {
  const beats: SceneSfxBeatRecipe[] = [{ atSec: 0.05, sound: "swoosh", volume: 0.35 }];
  for (let i = 0; i < 5; i++) {
    beats.push({ atSec: 0.2 + i * 0.12, sound: "tap", volume: 0.28, durSec: 0.22 });
  }
  beats.push({ atSec: clamp(durationSec - 0.5, 0.8, durationSec), sound: "ding", volume: 0.3 });
  return beats;
};

const stickerSlamRecipe: RecipeFn = ({ inputs, durationSec }) => {
  const n = Math.max(1, itemsLen(inputs) || 3);
  const stagger = clamp((durationSec - 0.4) / n, 0.25, 0.7);
  const beats: SceneSfxBeatRecipe[] = [];
  for (let i = 0; i < n; i++) {
    beats.push({ atSec: 0.05 + i * stagger, sound: "impact", volume: 0.42, label: `Slam ${i + 1}` });
  }
  return beats;
};

const counterRecipe: RecipeFn = ({ durationSec }) => {
  const beats: SceneSfxBeatRecipe[] = [];
  const ticks = Math.max(4, Math.min(12, Math.round(durationSec * 2)));
  for (let i = 0; i < ticks; i++) {
    beats.push({ atSec: i * (durationSec * 0.7 / ticks), sound: "tick", volume: 0.28, durSec: 0.15 });
  }
  beats.push({ atSec: clamp(durationSec - 0.3, 0.5, durationSec), sound: "ding", volume: 0.45, label: "Final" });
  return beats;
};

const tickerStripRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.0, sound: "woosh", volume: 0.32, durSec: durationSec },
];

const marqueeStackRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.05, sound: "woosh", volume: 0.32 },
  { atSec: clamp(durationSec - 0.5, 0.8, durationSec), sound: "transition", volume: 0.3 },
];

const gridRevealRecipe: RecipeFn = ({ durationSec }) => {
  const beats: SceneSfxBeatRecipe[] = [{ atSec: 0.0, sound: "swoosh", volume: 0.35 }];
  for (let i = 0; i < 6; i++) {
    beats.push({ atSec: 0.15 + i * 0.08, sound: "tap", volume: 0.25, durSec: 0.2 });
  }
  beats.push({ atSec: clamp(durationSec - 0.5, 0.8, durationSec), sound: "shimmer", volume: 0.3 });
  return beats;
};

const beforeAfterRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.1, sound: "swoosh", volume: 0.4, label: "Slider" },
  { atSec: clamp(durationSec - 0.6, 0.8, durationSec), sound: "ding", volume: 0.35 },
];

const bigNumberRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.0, sound: "riser", volume: 0.4 },
  { atSec: clamp(durationSec - 0.8, 0.6, durationSec - 0.3), sound: "impact", volume: 0.55 },
  { atSec: clamp(durationSec - 0.7, 0.7, durationSec), sound: "shimmer", volume: 0.3 },
];

/* ---------- Partner-Szenen ---------- */

const partnerRevealRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.05, sound: "swoosh", volume: 0.35 },
  { atSec: 0.5, sound: "shimmer", volume: 0.3 },
  { atSec: 1.0, sound: "tap", volume: 0.32 },
  { atSec: clamp(durationSec - 0.5, 1.2, durationSec), sound: "transition", volume: 0.35 },
];

const partnerServiceDetailRecipe: RecipeFn = ({ durationSec }) => [
  { atSec: 0.05, sound: "swoosh", volume: 0.36 },
  { atSec: 0.5, sound: "tap", volume: 0.32 },
  { atSec: 1.1, sound: "shimmer", volume: 0.28 },
  { atSec: clamp(durationSec - 0.6, 1.0, durationSec), sound: "transition", volume: 0.35 },
];

const partnerCardFlipRecipe: RecipeFn = ({ inputs, durationSec }) => {
  const n = Math.max(1, itemsLen(inputs) || 3);
  const stagger = clamp((durationSec - 0.8) / n, 0.4, 1.2);
  const beats: SceneSfxBeatRecipe[] = [{ atSec: 0.0, sound: "swoosh", volume: 0.32 }];
  for (let i = 0; i < n; i++) {
    beats.push({ atSec: 0.2 + i * stagger, sound: "woosh", volume: 0.35, durSec: 0.45, label: `Flip ${i + 1}` });
  }
  return beats;
};

const partnerCategoryLineupRecipe: RecipeFn = ({ inputs, durationSec }) => {
  const n = Math.max(1, itemsLen(inputs) || 6);
  const stagger = clamp((durationSec - 0.5) / n, 0.15, 0.5);
  const beats: SceneSfxBeatRecipe[] = [{ atSec: 0.0, sound: "swoosh", volume: 0.32 }];
  for (let i = 0; i < n; i++) {
    beats.push({ atSec: 0.15 + i * stagger, sound: "tap", volume: 0.28, durSec: 0.25 });
  }
  beats.push({ atSec: clamp(durationSec - 0.4, 0.8, durationSec), sound: "ding", volume: 0.32 });
  return beats;
};

/* ============================================================
 *  Registry + Public API
 * ============================================================ */

const RECIPES: Record<string, RecipeFn> = {
  // visual
  colorBlockReveal: colorBlockRevealRecipe,
  laptopMockup: laptopMockupRecipe,
  phoneMockup: phoneMockupRecipe,
  tabletMockup: tabletMockupRecipe,
  watchMockup: watchMockupRecipe,
  browserMockup: browserMockupRecipe,
  deviceTrio: laptopMockupRecipe,
  appStoreShowcase: phoneMockupRecipe,
  phoneCarousel: phoneMockupRecipe,
  phoneTilt3D: phoneMockupRecipe,
  phoneSplitCompare: beforeAfterRecipe,
  cardDeckFan: cardDeckFanRecipe,
  stickerSlam: stickerSlamRecipe,
  tickerStrip: tickerStripRecipe,
  marqueeStack: marqueeStackRecipe,
  gridReveal: gridRevealRecipe,
  beforeAfterSlider: beforeAfterRecipe,
  comparisonSplit: beforeAfterRecipe,
  categoryGrid: itemRevealRecipe,
  categoryDock: itemRevealRecipe,
  chatBubbleStack: listStackRecipe,
  productCardLift: itemRevealRecipe,
  fourStepLaunch: itemRevealRecipe,
  processArrow: itemRevealRecipe,
  timelineFlow: itemRevealRecipe,
  featureMatrix: itemRevealRecipe,
  trustSealStrip: itemRevealRecipe,
  floatingLabels: itemRevealRecipe,
  commissionFlow: itemRevealRecipe,
  marketplaceMap: itemRevealRecipe,
  cityPin: itemRevealRecipe,
  iconLine: itemRevealRecipe,
  noiseField: textRevealRecipe,
  geometricBurst: brandFlashRecipe,
  parallaxLayers: cinematicRevealRecipe,
  lottiePlay: textRevealRecipe,

  // intro
  brandFlash: brandFlashRecipe,
  cinematicHero: cinematicRevealRecipe,
  cinematicReveal: cinematicRevealRecipe,
  countdownIntro: countdownIntroRecipe,
  glitchIntro: glitchIntroRecipe,
  apertureOpen: cinematicRevealRecipe,
  gradientHero: textRevealRecipe,
  heroOrbit: introLogoRecipe,
  letterboxIntro: cinematicRevealRecipe,
  logoBeamStreak: introLogoRecipe,
  logoCircleSweep: introLogoRecipe,
  logoLiquidReveal: introLogoRecipe,
  logoOrbitLaunch: introLogoRecipe,
  logoPrismSplit: introLogoRecipe,
  logoRingBurst: introLogoRecipe,
  logoStamp: introLogoRecipe,
  pathDraw: cinematicRevealRecipe,

  // text
  typewriter: typewriterRecipe,
  quoteSlam: quoteSlamRecipe,
  flashStop: flashStopRecipe,
  gradientReveal: textRevealRecipe,
  handwritten: textRevealRecipe,
  highlightSweep: textRevealRecipe,
  kineticHook: textRevealRecipe,
  motionBlurReveal: textRevealRecipe,
  splitFrameStory: textRevealRecipe,
  splitHero: textRevealRecipe,

  // list
  bulletStack: listStackRecipe,

  // data
  bigNumber: bigNumberRecipe,

  // outro
  signOff: signOffRecipe,
  logoOutro: logoOutroRecipe,

  // partner
  partnerReveal: partnerRevealRecipe,
  partnerComingSoon: partnerRevealRecipe,
  partnerServiceDetail: partnerServiceDetailRecipe,
  partnerServiceList: partnerCategoryLineupRecipe,
  partnerCardFlip: partnerCardFlipRecipe,
  partnerCategoryLineup: partnerCategoryLineupRecipe,
};

/** Generisches Fallback-Rezept für Szenen ohne expliziten Eintrag. */
const fallbackRecipe: RecipeFn = ({ inputs, durationSec }) => {
  const n = itemsLen(inputs);
  if (n >= 2) return itemRevealRecipe({ sceneId: "", inputs, durationSec });
  return textRevealRecipe({ sceneId: "", inputs, durationSec });
};

/** Liefert sortierte, geclampte Beats für eine Szene. */
export function buildSceneSfxRecipe(params: {
  sceneId: string;
  inputs: SceneInputs;
  durationSec: number;
}): SceneSfxBeatRecipe[] {
  const fn = RECIPES[params.sceneId] ?? fallbackRecipe;
  const raw = fn(params);
  // Filter + sortieren: kein Beat darf hinter dem Szenenende liegen.
  return raw
    .map((b) => ({
      ...b,
      atSec: Math.max(0, Math.min(b.atSec, Math.max(0, params.durationSec - 0.05))),
      volume: b.volume ?? 0.4,
      durSec: b.durSec ?? defaultDurForSound(b.sound),
    }))
    .sort((a, b) => a.atSec - b.atSec);
}

/** Hat die Szene überhaupt ein dediziertes Rezept (sonst Fallback)? */
export function hasDedicatedSceneRecipe(sceneId: string): boolean {
  return Object.prototype.hasOwnProperty.call(RECIPES, sceneId);
}
